import { store$, store } from '@redux/store';
import {
  hasAvailableNow,
  hasReadyToBuild,
  searchParametersActions,
  searchParametersSelectors,
} from 'features/searchResultsFacets';
import { map, distinctUntilChanged, skip } from 'rxjs';

const { selectContentTemplateTypeModifiers } = searchParametersSelectors;

const contentFacets = [
  'adult',
  'builderbrand',
  'gatedcommunity',
  'golfcourse',
  'green',
  'hotdeals',
  'luxury',
  'condosandtownhomes',
  'natureareas',
  'parks',
  'pool',
  'schooldistrict',
  'singlefamily',
  'sportsfacilities',
  'views',
  'waterfront',
  'communityname',
  'price',
  'comingsoon',
  'moveindate',
  'customhomes',
  'manufactured',
  'modelhomes',
];

/**
 * The function returns a content template type based on the home status provided.
 * @param homeStatus - a string representing the status of a home plan, either "AvailableNow" or
 * "ReadyToBuild".
 * @returns The function `getContentTemplateTypeForMoveInDateFilters` returns a string value that
 * represents the content template type based on the `homeStatus` parameter. If `homeStatus` is equal
 * to `PlanStatus.AvailableNow.toString()`, it returns `'QuickMoveIn'`. If `homeStatus` is equal to
 * `PlanStatus.ReadyToBuild.toString()`, it returns `'ReadyToBuild
 */
const getContentTemplateTypeForMoveInDateFilters = (homeStatus) => {
  if (hasAvailableNow(homeStatus)) {
    return 'QuickMoveIn';
  }

  if (hasReadyToBuild(homeStatus)) {
    return 'ReadyToBuild';
  }

  return '';
};

/**
 * The function returns a content template type based on whether we have a brand id other than null.
 * @param brandId: A string representing the brand ID.
 * @returns The function `getContentTemplateTypeForBrandFilter` returns a string value that
 * represents the content template type.
 */
const getContentTemplateTypeForBrandFilter = (brandId) => {
  if (brandId !== null) {
    return 'builderbrand';
  }

  return '';
};

/**
 * getContentTemplateType: Gets the Content Template Type that is going to be used.
 * @param {Object} data Data related required to define the Content Template Type.
 * @returns A string with the Content Template Type.
 */
function getContentTemplateType(data) {
  const { productType, appliedFacetList, homeStatus, brandId } = data;

  // We have to check first about the product type because its priority is higher than the facets content.
  if (productType === 'AllProducts') {
    return 'BuilderDirectory';
  }

  // Here we are getting the facets that have a content template.
  const appliedContentFacetList = appliedFacetList.filter((facet) => contentFacets.includes(facet.toLowerCase()));

  const lastItem = appliedContentFacetList[appliedContentFacetList.length - 1];

  if (lastItem === 'moveindate') {
    const moveInDateContentTemplate = getContentTemplateTypeForMoveInDateFilters(homeStatus);
    return moveInDateContentTemplate;
  }
  
  if (lastItem === 'modelhomes') {
    return 'ModelHomes';
  }

  if (lastItem === 'customhomes') {
    return 'CustomHomes'
  }

  if (lastItem === 'builderbrand') {
    const builderContentTemplate = getContentTemplateTypeForBrandFilter(brandId);
    return builderContentTemplate;
  }

  return appliedContentFacetList.pop() || '';
}

const contentTemplateTypeModified$ = store$.pipe(
  map(selectContentTemplateTypeModifiers),
  distinctUntilChanged(),
  map(getContentTemplateType),
  distinctUntilChanged(),
  skip(1)
);

/**
 * updateContentTemplateType: Updates the content template type according to the last facet with content.
 * @param {string} contentTemplateType Content template type to show.
 */
function updateContentTemplateType(contentTemplateType) {
  store.dispatch(searchParametersActions.fetchContentTemplateType(contentTemplateType));
}

contentTemplateTypeModified$.subscribe(updateContentTemplateType);
